import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import _ from 'lodash';
import { IPostModel } from '../../models';
import { postService } from '../../services/post.service';
import { LocalStorageService } from '../../configuration';
import { getSeen, MAX_SHOW } from './reels-seen';

// Feed cho Reels — POST làm nền, VIDEO rải ĐỀU xen vào (không dồn thành cục).
// Nền = home-post (đã đăng nhập: cá nhân hoá; chưa: public). Video thêm = feed video-only.
// Lọc video ĐÃ XEM ra (ưu tiên video mới); chỉ cho lặp lại tối đa 2 lần khi quá ít video mới.

const isVid = (p: IPostModel) =>
  p.postType === 'video' && (p.postVideoResponses?.length || 0) > 0;

// Lọc VIDEO: ưu tiên chưa xem; nếu quá ít (<2) mới cho video đã xem quay lại, chặn quá MAX_SHOW lần.
const filterSeenVideos = (videos: IPostModel[]): IPostModel[] => {
  const seen = getSeen();
  const fresh = videos.filter((v) => (seen[v.id] || 0) === 0);
  if (fresh.length >= 2) return fresh;
  return videos.filter((v) => (seen[v.id] || 0) < MAX_SHOW);
};

// Trộn ĐỀU theo tỉ lệ (merge): rải video giữa post đều đặn, không cụm ở đầu/cuối.
const interleaveEven = (posts: IPostModel[], videos: IPostModel[]): IPostModel[] => {
  const out: IPostModel[] = [];
  let pi = 0;
  let vi = 0;
  const P = posts.length;
  const V = videos.length;
  while (pi < P || vi < V) {
    // So tiến độ tương đối: bên nào "chậm" hơn thì lấy trước → phân bố đều.
    const takeVideo =
      vi < V && (pi >= P || vi / V <= pi / (P || 1));
    if (takeVideo) out.push(videos[vi++]);
    else if (pi < P) out.push(posts[pi++]);
    else out.push(videos[vi++]);
  }
  return out;
};

const isLoggedIn = (): boolean => {
  const token = LocalStorageService.getItem('accessToken');
  if (!token) return false;
  try {
    const roles = jwtDecode<{ Role?: string[] }>(token).Role;
    return !!roles && roles.length > 0;
  } catch {
    return false;
  }
};

export const useReelsFeed = ({ pageNumber }: { pageNumber: number }) => {
  const [data, setData] = useState<IPostModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        const emptyPage = { data: [] as IPostModel[], hasMore: false };
        const [base, vids] = await Promise.all([
          (isLoggedIn()
            ? postService.getPosts({ pageNumber, pageSize: 10 })
            : postService.getPublicPosts({ pageNumber, pageSize: 10 })
          ).catch(() => emptyPage),
          postService
            .getPostsTypeVideo({ pageNumber, pageSize: 10 })
            .catch(() => emptyPage),
        ]);
        if (cancelled) return;

        // Gộp nền + video-only, tách post/video, lọc video đã xem, rồi trộn đều.
        const all = [...(base.data || []), ...(vids.data || [])];
        const posts = _.uniqBy(all.filter((p) => !isVid(p)), 'id');
        const videos = filterSeenVideos(_.uniqBy(all.filter(isVid), 'id'));
        const mixed = interleaveEven(posts, videos);
        setData((prev) => _.uniqBy([...prev, ...mixed], 'id'));
        setHasMore((base.hasMore || (vids as any).hasMore) ?? false);
      } catch {
        // giữ nội dung đã có
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [pageNumber]);

  return { data, loading, hasMore };
};
